import RestUtilities from './Base'
import AuthStore from '../stores/Auth'

import jwt_decode from 'jwt-decode'
import UserInfo, { CreateUserInfo } from '../viewModels/UserInfo'
import { USER_ROLE } from '../signin/RoleContext'

export default class Auth {
  static isSignedInIn() {
    return !!AuthStore.getToken()
  }
  static getToken() {
    return AuthStore.getToken()
  }

  static getRoles(): USER_ROLE[] {
    const options = Auth.getTokenFieldArray('http://schemas.microsoft.com/ws/2008/06/identity/claims/role');
    const srcArray = Array.isArray(options) ? options : [options];
    const parsed = srcArray.filter(a => Object.values(USER_ROLE).some(x => x === a)).map((a: string) => Object.values(USER_ROLE).find(x => x === a)) as USER_ROLE[];

    return parsed;
  }

  static checkRoleMembership(roleName: string[]) : boolean {
    const options = Auth.getTokenFieldArray('http://schemas.microsoft.com/ws/2008/06/identity/claims/role');
    const srcArray = Array.isArray(options) ? options : [options];

    return srcArray.some((r) => roleName.some(b => r.toLowerCase() === b.toLowerCase()));
  }

  static getLP() {
    return { id: Auth.getTokenFieldNumeric('lp_id'), name: Auth.getTokenField('lp_name') }
  }

  static getTokenField(field_name: string) : string {
    const token = Auth.getToken()
    if (token) {
      try {
        const decoded: any = jwt_decode(token)
        return decoded[field_name]
      }
      catch (e) {
        return ''
      }

    }
    return ''
  }

  static getTokenFieldNumeric(field_name: string): number {
    return Number(Auth.getTokenField(field_name));
  }

  static getTokenFieldArray(field_name: string) : string[] {
    const token = Auth.getToken()
    if (token) {
      try {
        const decoded: any = jwt_decode(token)
        return decoded[field_name] as string[];
      }
      catch {
        return [];
      }

    }
    return [];
  }

  static getFirstName() {
    return Auth.getTokenField('FirstName')
  }

  static getLastName() {
    return Auth.getTokenField('LastName')
  }

  static getEmail() {
    return Auth.getTokenField(
      'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
    )
  }

  static getExpiry() {
    return Auth.getTokenField(
      'http://schemas.microsoft.com/ws/2008/06/identity/claims/expiration'
    )
  }


  static async getMFAConfirm(mode: string | null, contact: string | null): Promise<string> {
    if (!mode || !contact) {
      return "failed";
    }

    return RestUtilities.get(`/api/auth/getMFAConfirm?mode=${mode}&contact=${contact}`).then((response) => {
      return !response.is_error ? response.content : "failed";
    });
  }

  static async getMFAConfirmForUser(userId: number, mode: string | null, contact: string | null): Promise<string> {
    if (!(userId && mode  && contact)) {
      return "failed";
    }

    return RestUtilities.get(`/api/auth/getMFAConfirmForUser?userId=${userId}&mode=${mode}&contact=${contact}`).then((response) => {
      return !response.is_error ? response.content : "failed";
    });
  }

  static async UpdateMFAInfo(mode: string | null, contact: string | null): Promise<boolean> {
    if (!(mode && contact)) {
      return false;
    }

    const response = await RestUtilities.post(`/api/auth/updateMFAInfo`,
      `mode=${mode}&contact=${contact}`,
    true);

    return !response.is_error ? response.content : false;
  }

  static async UpdateMFAInfoForUser(userId: number | null, mode: string | null, contact: string | null): Promise<boolean> {
    if (!(userId && mode && contact)) {
      return false;
    }

    const response = await RestUtilities.post(`/api/auth/updateMFAInfoForUser`,
      `userId=${userId}&mode=${mode}&contact=${contact}`,
      true);

    return !response.is_error ? response.content : false;
  }

  static async updateUserInfo(userInfo: UserInfo) : Promise<boolean> {
    const response = await RestUtilities.post(`/api/auth/updateUserInfo`,
      userInfo,
      true);

    return !response.is_error ? response.content : false;
  }

  static async createUser(createInfo: CreateUserInfo): Promise<string> {
    const response = await RestUtilities.post(`/api/auth/createUser`,
      createInfo,
      true);

    return !response.is_error ? response.content : "please resubmit";
  }

  static async deleteUser(userId: number): Promise<string> {
    const response = await RestUtilities.post(`/api/auth/deleteUser?userId=${userId}`,{},true);

    return !response.is_error ? response.content : "Could not delete user - please refresh and try again";
  }

  static signOut() {
    AuthStore.clear()
    sessionStorage.clear();
  }

  static signInOrRegister(
    email: string,
    password: string,
    mfa: string | undefined,
    rememberMe: boolean,
    isRegister: boolean = false
  ) {
    return RestUtilities.post(
      `/api/auth/${isRegister ? 'register' : 'login'}`,
      {
        username: email,
        password: password,
        mfa: (mfa == undefined ? '' : mfa),
        rememberMe: rememberMe,
        grant_type: !isRegister ? 'password' : ''
      }, true).then((response) => {
      if (!response.is_error) {
        AuthStore.setResponse(response.content)
      }
      return response
    })
  }

  static signIn(email: string, password: string, mfa: string|undefined, rememberMe: boolean) {
    // return this.mockSignInOrRegister(email, password, false)
    return this.signInOrRegister(email, password, mfa, rememberMe, false);
  }
  /*
  register(email: string, password: string) {
    return this.signInOrRegister(email, password, true)
  }
  */

  static refreshToken() {
    return RestUtilities.get('/api/auth/refresh').then((response) => {
      if (!response.is_error) {
        AuthStore.setResponse(response.content)
      }
      return response
    })
  }

  static changePassword(currentPassword: string, password: string) {
    return RestUtilities.post('/api/auth/changePassword', {
        password: password,
        currentPassword: currentPassword
      },
        true
      ).then((response) => {
        return response
      });
  }

  static changePasswordForUser(userId: number, password: string) {
    return RestUtilities.post('/api/auth/changePasswordForUser', {
      password: password,
      userId: userId
    },
      true
    ).then((response) => {
      return response
    });
  }

  static getLoggedInUserInfo(): Promise<UserInfo|void> {
    if (!Auth.isSignedInIn())
      return Promise.resolve();

    return RestUtilities.get('/api/auth/getUserInfo').then((response: any) => {
      const retVal = response.content;

      if (retVal) {
        retVal.expiry = retVal.expiry ? new Date(retVal.expiry + "Z") : null;
        retVal.lastChanged = retVal.lastChanged ? new Date(retVal.lastChanged + "Z") : null;
        retVal.previousSignOn = retVal.previousSignOn ? new Date(retVal.previousSignOn + "Z") : null;
        retVal.lastSignOn = retVal.lastSignOn ? new Date(retVal.lastSignOn + "Z") : null;
      }

      return retVal as UserInfo;
    });
  }

  static getUserInfo(userId: number): Promise<UserInfo | void> {
    if (!Auth.isSignedInIn())
      return Promise.resolve();

    return RestUtilities.get(`/api/auth/getUserInfo/${userId}`).then((response: any) => {
      const retVal = response.content;

      if (retVal) {
        retVal.expiry = retVal.expiry ? new Date(retVal.expiry + "Z") : null;
        retVal.lastChanged = retVal.lastChanged ? new Date(retVal.lastChanged + "Z") : null;
        retVal.previousSignOn = retVal.previousSignOn ? new Date(retVal.previousSignOn + "Z") : null;
        retVal.lastSignOn = retVal.lastSignOn ? new Date(retVal.lastSignOn + "Z") : null;
      }

      return retVal as UserInfo;
    });

  }

  static getAccountListing(lpId : number) {
    if (!Auth.isSignedInIn())
      return Promise.resolve();

    return RestUtilities.get(`/api/${lpId}/account/users`);
  }
}
